import React from "react";
import PropTypes from "prop-types";
import ScheduleOutlinedIcon from "@mui/icons-material/ScheduleOutlined";
import StopCircleOutlinedIcon from "@mui/icons-material/StopCircleOutlined";
import ReplayOutlinedIcon from "@mui/icons-material/ReplayOutlined";
import { NodeSection, NodeSelect, NodeTextField } from "../../NodeUI";
import ToggleRow from "../../common/ToggleRow";
import MissionReadOnlyDurationField from "../agentConfig/tabs/mission/MissionReadOnlyDurationField";
import MissionPolicyWakeFields from "./MissionPolicyWakeFields";
import { COMPLETION_MODE_OPTIONS, STOP_KIND_OPTIONS } from "./policy/missionPolicyDraft";
import "../agentConfig/tabs/mission/MissionHarness.css";

/**
 * Editable wake / stop / turn policy fields, validated against platform safety caps on save.
 * Shared by SessionMissionDialog, MissionTemplateForm, MissionConfigPage, and agent config.
 */
export default function MissionPolicyFields({
  draft,
  onChange,
  grouped = false,
  readOnly = false,
  hideCompletionMode = false,
  hideAllowAgentReschedule = false,
}) {
  const set = (key, value) => {
    if (readOnly) return;
    onChange(key, value);
  };

  const stopFields = (
    <>
      <NodeSelect
        label="Stop when"
        value={draft.stopKind}
        options={STOP_KIND_OPTIONS}
        inDialog
        disabled={readOnly}
        onChange={(value) => set("stopKind", value)}
      />
      <div className="mission-harness__stop-caps-row">
        <NodeTextField
          label="Max turns"
          type="number"
          value={draft.maxTurns}
          onChange={(value) => set("maxTurns", value)}
          inputProps={{ min: 1 }}
          disabled={readOnly}
          helperText="Safety cap on wake-driven turns."
        />
        <MissionReadOnlyDurationField
          label="Max duration"
          isoValue={draft.maxDuration}
          fallbackUnit="days"
          disabled={readOnly}
          inDialog
          inline
          onChange={(iso) => set("maxDuration", iso)}
        />
      </div>
      {!hideCompletionMode ? (
        <NodeSelect
          label="Completion mode"
          value={draft.completionMode}
          options={COMPLETION_MODE_OPTIONS}
          inDialog
          disabled={readOnly}
          onChange={(value) => set("completionMode", value)}
        />
      ) : null}
    </>
  );

  const turnFields = (
    <>
      <NodeTextField
        label="Max iterations per turn"
        type="number"
        value={draft.maxIterations}
        onChange={(value) => set("maxIterations", value)}
        inputProps={{ min: 1 }}
        disabled={readOnly}
        helperText="Tool-call budget for a single wake."
      />
      <NodeTextField
        label="Wake message"
        value={draft.wakeMessage}
        onChange={(value) => set("wakeMessage", value)}
        multiline
        minRows={2}
        maxRows={4}
        disabled={readOnly}
        helperText="Default instruction injected when a wake dispatches a turn."
      />
      {!hideAllowAgentReschedule ? (
        <ToggleRow
          checked={draft.allowAgentReschedule}
          onChange={(value) => set("allowAgentReschedule", value)}
          title="Allow agent reschedule"
          description="Let the agent create its own schedules. Off keeps cadence under mission policy."
        />
      ) : null}
      <ToggleRow
        checked={draft.postCheckToTranscript}
        onChange={(value) => set("postCheckToTranscript", value)}
        title="Post-check to transcript"
        description="Write the per-turn progress check into the transcript instead of the activity log only."
      />
    </>
  );

  if (!grouped) {
    return (
      <>
        <MissionPolicyWakeFields draft={draft} onChange={onChange} readOnly={readOnly} />
        {stopFields}
        {turnFields}
      </>
    );
  }

  return (
    <div className="mission-harness__policy-stack">
      <NodeSection
        title="Wake policy"
        icon={<ScheduleOutlinedIcon className="mission-harness__section-icon" />}
      >
        <MissionPolicyWakeFields draft={draft} onChange={onChange} readOnly={readOnly} />
      </NodeSection>

      <NodeSection
        title="Stop policy"
        icon={<StopCircleOutlinedIcon className="mission-harness__section-icon" />}
      >
        {stopFields}
      </NodeSection>

      <NodeSection
        title="Turn policy"
        icon={<ReplayOutlinedIcon className="mission-harness__section-icon" />}
      >
        {turnFields}
      </NodeSection>
    </div>
  );
}

MissionPolicyFields.propTypes = {
  draft: PropTypes.object.isRequired,
  onChange: PropTypes.func.isRequired,
  grouped: PropTypes.bool,
  readOnly: PropTypes.bool,
  hideCompletionMode: PropTypes.bool,
  hideAllowAgentReschedule: PropTypes.bool,
};
